import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaInstagram, FaLinkedinIn } from "react-icons/fa";
import Button from "./Button";
import Input from "./Input";
import TextAnimP1 from "./TextAnimP1";

// Animation variants for sections
const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

// Variants for staggered children (form inputs, links, social icons)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.4,
    },
  },
};

const childVariants = {
  hidden: { opacity: 0, scale: 1.2, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

// Social icons slide from left
const socialIconVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

export default function Footer({ popActive, setPopActive }) {
  const footerRef = useRef(null);
  const initFormData = {
    Full_Name: "",
    Company_Name: "",
    email: "",
    // countryCode: "",
    Contact_Number: "",
  };

  const [formData, setFormData] = useState(initFormData);
  const [message, setMessage] = useState("");
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(true);

  // Navigation items (same as Navigation component)
  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    // { path: "/blogs", label: "Blogs" },
    { path: "/contact", label: "Contact" },
  ];

  // Services/Expertise data (same as Navigation component)
  const expertiseItems = [
    { path: "/design", label: "Design" },
    { path: "/technology", label: "Technology" },
    { path: "/marketing", label: "Marketing" },
  ];

  useEffect(() => {
    setShowContactForm(location.pathname !== "/contact");
  }, [location.pathname]);

  const validateForm = () => {
    const requiredFields = [
      "Full_Name",
      "Company_Name",
      "email",
      "Contact_Number",
    ];

    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === "") {
        const formattedField = field
          .replace(/([A-Z])/g, " $1") // Add space before capital letters
          .replace("_", " ") // In case there's any underscore
          .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize each word
        return `❌ Please fill out "${formattedField}"`;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      return "❌ Please enter a valid email address.";
    }

    return null;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      const error = validateForm();
      if (error) {
        setMessage(error);
        return;
      }

      const response = await fetch(
        "https://formsubmit.co/ajax/Dominance@digicots.com",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      if (result) {
        setPopActive(true);
        setFormData(initFormData);
      }
    } catch (err) {
      setMessage("❌ Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      ref={footerRef}
      className=" dark:text-white bg-white dark:bg-transparent py-16 xl:px-35 px-10  relative overflow-hidden "
    >
      <div className="container mx-auto max-w-[1600px] relative">
        

        {/* Bottom Section - Footer Content */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5"
        >
          {/* Branding */}
          <div className="flex flex-col items-center md:items-start col-span-2 md:col-span-1">
            <h3
              className="font-bold text-[#EE005B]"
              variants={childVariants}
            >
              <Link to={"/"}>
                <img src="/new-final-data/logo/logo-1.svg" className="w-[400px] invert relative  dark:invert-0 " alt="" />
                {/* <img src="/images/logo-black.svg" className="w-[400px]" alt="" /> */}
              </Link>
            </h3>
            <div
              className="flex space-x-3 mt-4 raleway"
              variants={containerVariants}
            >
              {[
                {
                  title: "instagram",
                  link: "https://www.instagram.com/digicots_/",
                  icon: <FaInstagram />,
                },
                {
                  title: "linkedin-in",
                  link: "https://www.linkedin.com/company/digicots-interactive-private-limited/",
                  icon: <FaLinkedinIn />,
                },
              ].map((socialIcon, i) => (
                <div
                  key={i}
                  className="w-8 h-8 dark:bg-gray-800 rounded-md flex justify-center items-center pointer-events-auto"
                  variants={socialIconVariants}
                >
                  <a href={socialIcon.link} target="_blank">
                    {socialIcon.icon}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Address */}
          <div
            className="sm:text-left sm:max-w-[250px] xl:ml-10 col-span-2 sm:col-span-1 text-center md:text-left"
            variants={childVariants}
          >
            <h4 className="text-lg font-semibold raleway">USA</h4>
            <p className="dark:text-gray-400 mt-2  raleway">
              <a href="">
                3422 Old Capitol Trail <br /> Wilmington DE 19808 , USA
              </a>
            </p>
            <p className="mt-2  dark:text-gray-400 raleway">+1 408-676-5986</p>
            <h4 className="text-lg font-semibold raleway mt-5">India</h4>
            <p className="dark:text-gray-400 mt-2  raleway">
              <a href="">
                B 73, Block B, Sector 57 <br /> Noida, Uttar Pradesh, India
              </a>
            </p>
            <p className="mt-2  dark:text-gray-400 raleway">+91 8630116684 <br/> +91 8826288647</p>
          </div>

          {/* Links */}
          <div
            className="flex justify-center text-center xl:text-left sm:justify-end gap-5 md:gap-10 lg:gap-20  md:justify-around md:text-left raleway"
            variants={containerVariants}
          >
            {/* Expertise Links */}
            <div>
              <h4 className="font-semibold" variants={childVariants}>
                Expertise
              </h4>
              <ul
                className="dark:text-gray-400 mt-2 space-y-1"
                variants={containerVariants}
              >
                {expertiseItems.map((link, i) => (
                  <li key={i} variants={childVariants}>
                    <Link
                      to={link.path}
                      className="dark:hover:text-white hover:text-zinc-400 pointer-events-auto"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold" variants={childVariants}>
                Quick Links
              </h4>
              <ul
                className="dark:text-gray-400 mt-2 space-y-1 pointer-events-auto"
                variants={containerVariants}
              >
                {navItems.map((link, i) => (
                  <li key={i} variants={childVariants}>
                    <Link
                      to={link.path}
                      className="dark:hover:text-white hover:text-zinc-400"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div
          className="mt-12 dark:text-gray-400 flex flex-col sm:flex-row justify-between align-middle"
        >
          <div>
            <p className="text-center sm:text-start raleway">
              Designed by{" "}
              <a href="#" className="text-[#EE005B]">
                Team Digicots
              </a>
            </p>
            <p className="mt-1 text-center sm:text-start raleway">
              Created Proudly in India
            </p>
          </div>
          <div className="text-center sm:text-end raleway">
            <p className="mt-1">
              Copyright 2025 © <br /> All Rights Reserved. All Wrongs Reversed.
            </p>
          </div>
        </div>
        {/* </TextAnimP1> */}

      </div>
    </footer>
  );
}
